/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.JOB.LIST — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/JobDescriptionPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail) over the
 * SAME data/logic layer: reads via apiDb (csm_job_description + _tl for names,
 * plus the csm_approval_hierarchy + _tl roll-up so the FK resolves to a
 * translated hierarchy name — never a raw id, §17.B4). The desktop role /
 * hierarchy editors are intentionally dropped here; the detail sheet is
 * read-only. §16: never the desktop grid re-hosted.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Briefcase, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { MobileRelatedChildren, type MobileChildGroup, type MobileChildItem } from '../components/MobileRelatedChildren';
import { buildMobileTranslationsGroup } from '../components/mobile_translations_group';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill, type MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

interface MobileJobRow {
  job_description_id: number;
  internal_code: string;
  is_active: 'Y' | 'N';
  apr_status: string;
  display_text: string;
  hierarchy_label: string;
  /** Every locale's text, kept so the related section can list them (§17.B14). */
  tl: TlRow[];
}

type StatusFacet = 'all' | 'active' | 'inactive';

interface TlRow {
  locale_code: string;
  display_text: string;
}

// Approval-state metadata: the stored value stays the code; only the label is
// localized (§17.B11). Pill variants reuse the shared m-pill modifiers.
const APR_STATUS_META: Record<string, { labelKey: string; label: string; tone: MobilePillTone }> = {
  APPROVED: { labelKey: 'mobile.jobs.apr_status.APPROVED', label: 'Approved', tone: 'ok' },
  PENDING_APPROVAL: { labelKey: 'mobile.jobs.apr_status.PENDING_APPROVAL', label: 'Pending approval', tone: 'wait' },
  REJECTED: { labelKey: 'mobile.jobs.apr_status.REJECTED', label: 'Rejected', tone: 'no' },
};

function resolveTl(tls: TlRow[] | undefined, locale: string, fallback: string): string {
  const list = tls ?? [];
  const exact = list.find((tl) => tl.locale_code === locale);
  const fb = list.find((tl) => tl.locale_code === 'EN_US') ?? list[0];
  return exact?.display_text ?? fb?.display_text ?? fallback;
}

export function MobileJobDescriptionPage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [jobs, setJobs] = useState<MobileJobRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileJobRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Related children (lazy-loaded when the detail sheet opens).
  const [childGroups, setChildGroups] = useState<MobileChildGroup[]>([]);
  const [childBusy, setChildBusy] = useState(false);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const locale = activeLocale;
      const { data, error: e } = await apiDb
        .from('csm_job_description')
        .select(`
          job_description_id, internal_code, is_active, apr_status, approval_hierarchy_id,
          csm_job_description_tl ( locale_code, display_text ),
          csm_approval_hierarchy:approval_hierarchy_id (
            internal_code, csm_approval_hierarchy_tl ( locale_code, display_text )
          )
        `)
        .order('internal_code');
      if (e) throw e;

      type RawJob = {
        job_description_id: number;
        internal_code: string;
        is_active: 'Y' | 'N';
        apr_status: string;
        approval_hierarchy_id: number | null;
        csm_job_description_tl?: TlRow[];
        csm_approval_hierarchy?: { internal_code?: string; csm_approval_hierarchy_tl?: TlRow[] } | null;
      };
      const rows: MobileJobRow[] = ((data ?? []) as RawJob[]).map((j) => ({
        job_description_id: j.job_description_id,
        internal_code: j.internal_code,
        is_active: j.is_active,
        apr_status: j.apr_status,
        display_text: resolveTl(j.csm_job_description_tl, locale, j.internal_code),
        tl: j.csm_job_description_tl ?? [],
        hierarchy_label:
          j.approval_hierarchy_id == null
            ? ''
            : resolveTl(
                j.csm_approval_hierarchy?.csm_approval_hierarchy_tl,
                locale,
                j.csm_approval_hierarchy?.internal_code ?? '',
              ),
      }));
      setJobs(rows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  // Lazy-load the selected job description's children when the detail sheet opens:
  // the roles it maps to, the users who hold it, and its approvers (in sequence
  // order). Names resolved via _tl against the active locale with EN_US fallback.
  useEffect(() => {
    if (!selected) { setChildGroups([]); setChildBusy(false); return; }
    const jobId = selected.job_description_id;
    let cancelled = false;
    setChildBusy(true);
    void (async () => {
      const one = <T,>(v: T | T[] | undefined): T | undefined => (Array.isArray(v) ? v[0] : v);
      const pickTl = (tls: TlRow[] | undefined, fb: string) => {
        const list = tls ?? [];
        return list.find((x) => x.locale_code === activeLocale)?.display_text
          ?? list.find((x) => x.locale_code === 'EN_US')?.display_text
          ?? list[0]?.display_text ?? fb;
      };
      const byLabel = (a: MobileChildItem, b: MobileChildItem) => a.label.localeCompare(b.label);
      try {
        const [jdr, usr, jda] = await Promise.all([
          apiDb.from('csm_job_description_role')
            .select('role_id, csm_role_master ( internal_code, csm_role_master_tl ( locale_code, display_text ) )')
            .eq('job_description_id', jobId),
          apiDb.from('csm_user_master')
            .select('user_id, display_name, email')
            .eq('job_description_id', jobId),
          apiDb.from('csm_job_description_approver')
            .select('user_id, sequence_order, csm_user_master ( display_name, email )')
            .eq('job_description_id', jobId)
            .order('sequence_order'),
        ]);

        type RoleEmbed = { internal_code: string; csm_role_master_tl?: TlRow[] };
        type JdrRow = { role_id: number; csm_role_master?: RoleEmbed | RoleEmbed[] };
        const roles: MobileChildItem[] = ((jdr.data ?? []) as JdrRow[]).map((r) => {
          const role = one(r.csm_role_master);
          return { id: r.role_id, label: pickTl(role?.csm_role_master_tl, role?.internal_code ?? `#${r.role_id}`), sub: role?.internal_code };
        }).sort(byLabel);

        type UserRow = { user_id: number; display_name: string | null; email: string | null };
        const users: MobileChildItem[] = ((usr.data ?? []) as UserRow[])
          .map((u) => ({ id: u.user_id, label: u.display_name ?? u.email ?? `#${u.user_id}`, sub: u.email ?? undefined }))
          .sort(byLabel);

        type ApproverEmbed = { display_name: string; email: string };
        type JdaRow = { user_id: number; sequence_order: number; csm_user_master?: ApproverEmbed | ApproverEmbed[] };
        const approvers: MobileChildItem[] = ((jda.data ?? []) as JdaRow[]).map((r) => {
          const u = one(r.csm_user_master);
          return { id: r.user_id, label: u?.display_name ?? u?.email ?? `#${r.user_id}`, sub: u?.email, badges: [String(r.sequence_order)] };
        });

        if (cancelled) return;
        setChildGroups([
          { key: 'roles', table: 'csm_role_master', pkColumn: 'role_id', title: t('mobile.jobs.related.roles', 'Roles'), items: roles },
          { key: 'users', table: 'csm_user_master', pkColumn: 'user_id', title: t('mobile.jobs.related.users', 'Users'), items: users },
          { key: 'approvers', table: 'csm_user_master', pkColumn: 'user_id', title: t('mobile.jobs.related.approvers', 'Approvers'), items: approvers },
          buildMobileTranslationsGroup(selected.tl, t, activeLocale),
        ]);
      } catch {
        if (!cancelled) setChildGroups([]);
      } finally {
        if (!cancelled) setChildBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return jobs.filter((j) => {
      if (statusFacet === 'active' && j.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && j.is_active !== 'N') return false;
      if (!q) return true;
      return (
        j.display_text.toLowerCase().includes(q) ||
        j.internal_code.toLowerCase().includes(q) ||
        j.hierarchy_label.toLowerCase().includes(q)
      );
    });
  }, [jobs, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.jobs.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const aprLabel = (status: string): string => {
    const meta = APR_STATUS_META[status];
    return meta ? t(meta.labelKey, meta.label) : status || '—';
  };
  const aprTone = (status: string): MobilePillTone => APR_STATUS_META[status]?.tone ?? 'info';

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.jobs.title', 'Job Descriptions')}
        description={t('mobile.jobs.description', 'Roles, their approval state and the hierarchy that signs them off.')}
      />

      <MobileStatStrip
        stats={[
          { value: jobs.length, label: t('mobile.jobs.stat.total', 'Total') },
          { value: jobs.filter((j) => j.is_active === 'Y').length, label: t('mobile.jobs.stat.active', 'Active') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.jobs.search', 'Search job descriptions')}
          aria-label={t('mobile.jobs.search', 'Search job descriptions')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.jobs.none', 'No job descriptions match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((j) => (
            <MobileCardRow
              key={j.job_description_id}
              lead={<Briefcase />}
              leadTone="slate"
              title={j.display_text}
              subtitle={j.internal_code}
              trailing={
                <MobileStatusPill tone={j.is_active === 'Y' ? 'ok' : 'no'}>
                  {j.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(j)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.jobs.detail', 'Job description detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Briefcase size={20} /></span>
              <span>
                <div className="m-nm">{selected.display_text}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.jobs.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.jobs.approval', 'Approval')}
                value={<MobileStatusPill tone={aprTone(selected.apr_status)}>{aprLabel(selected.apr_status)}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.jobs.hierarchy', 'Hierarchy')}
                value={selected.hierarchy_label || t('common.none', 'None')}
              />
            </MobileContentCard>
            <MobileRelatedChildren groups={childGroups} busy={childBusy} session={session} />
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
                {t('common.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
